import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CALCULATIONS } from "../../locales/namespaces";
import {
    Box,
    IconButton,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody
} from "@mui/material";
import moment from "moment";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { calculateInstallments } from "../../helpers/calculations";
import RepaymentsForm from "../repaymentsForm/repaymentsForm";
import useUserData from "../../../../hooks/user/useUserData";
import { NumericFormat } from "react-number-format";
import { useLocation } from "react-router";

const RepaymentsList = ({
    repayments,
    handleRemoveRepayment,
    handleResults,
    calculationData,
    contributions,
    updateRepayments,
    handleUpdateRepayments
}) => {
    const { t } = useTranslation(CALCULATIONS);
    const { thousandSeparator, decimalSeparator } = useUserData()

    const [openPopup, setOpenPopup] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [repaymentsEdit, setRepaymentsEdit] = useState([])
    const location = useLocation()

    const calculateAfterDelete = (id) => {
        handleResults(calculateInstallments(
            0,
            calculationData.FixedDuration,
            calculationData.FloatingDuration,
            calculationData.FixedInterestRate,
            Number(calculationData.Spread) + Number(calculationData.Euribor),
            contributions.find(f => f.id === calculationData.Contribution).Value,
            calculationData.LoanAmount,
            calculationData.isMonthly === 'isMonthly',
            calculationData.isHidePreviousInstallments,
            moment(calculationData.DateInstallment).format('MM/DD/YYYY'),
            [...repayments.filter(repayment => repayment.id !== id)].map(repayment => ({
                Amount: Number(repayment.Amount),
                NewRate: Number(repayment.NewRate),
                Repayment_dt: moment(repayment.PaymentDt).format('MM/DD/YYYY')
            })),
            [],
            [],
            0,
            0,
            0,
            0,
            [...repayments.filter(repayment => repayment.id !== id)].map(repayment => ({
                Amount: Number(repayment.Amount),
                NewRate: Number(repayment.NewRate),
                Repayment_dt: moment(repayment.PaymentDt).format('MM/DD/YYYY')
            }))
        ));
    }
    const handleDelete = (id) => {
        handleRemoveRepayment(id, calculateAfterDelete);
    };

    // Function to open the popup and set the selected item ID
    const onOpenRepayments = (item) => {
        setSelectedItemId(item.id);
        setOpenPopup(true);
        setRepaymentsEdit({
            Amount: Number(item.Amount),
            NewRate: Number(item.NewRate),
            Repayment_dt: moment(item.PaymentDt),
            id: item.id
        })
    };

    // Function to close the popup
    const onCloseRepayments = () => {
        setSelectedItemId(null);
        setOpenPopup(false);
    };

    const sortedRepayments = useMemo(() => {
        return [...repayments].sort((a, b) =>
            moment(a.PaymentDt).valueOf() - moment(b.PaymentDt).valueOf()
        );
    }, [repayments]);

    const rows = useMemo(
        () =>
            sortedRepayments.map((item, index) => (
                <TableRow hover key={item.id} sx={{'& th':{color:'#485476', fontSize: '12px !important', paddingTop:'7px !important', paddingBottom:'7px !important' }, '& td':{ fontSize: '12px !important', paddingTop:'7px !important', paddingBottom:'7px !important' } }}>
                    <TableCell sx={{ pl: 3, color:'#8891ac !important' }} component="th" scope="row" className="table-cell-limit">
                        {index + 1}
                    </TableCell>
                    <TableCell sx={{ pl: 3 }} component="th" scope="row" className="table-cell-limit">
                        {moment(item.PaymentDt).format("MM/YYYY")}
                    </TableCell>
                    <TableCell align="right" className="table-cell-limit">
                        {
                            <NumericFormat
                                thousandSeparator={thousandSeparator}
                                decimalSeparator={decimalSeparator}
                                displayType="text"
                                value={item.Amount ? Number(item.Amount).toFixed(2) : '-'}
                            />
                        }
                    </TableCell>
                    <TableCell align="right" className="table-cell-limit">
                        <IconButton onClick={() => onOpenRepayments(item)} size={"small"} sx={{ backgroundColor: 'white', border: '1px solid #e8d4d6', borderRadius: '8px', height: '38px', width: '38px', '& svg': { color: '#485476', padding: '5px 4px' } }}>
                            <EditIcon />
                        </IconButton>
                    </TableCell>
                    <TableCell align="right" className="table-cell-limit">
                        <IconButton onClick={() => handleDelete(item.id)} color={"error"} size={"small"} sx={{ backgroundColor: 'white', border: '1px solid #e8d4d6', borderRadius: '8px', height: '38px', width: '38px', '& svg': { color: '#485476', padding: '5px 4px' } }}>
                            <DeleteIcon />
                        </IconButton>
                    </TableCell>
                </TableRow>
            )),

        [repayments, handleDelete, repaymentsEdit]
    );

    return (
        <Box sx={{ pt: 1, pb: 1 }}>
            <TableContainer className="table-full-width">
                <Table className="table-full-width" aria-label="repayments table">
                    <TableHead sx={{ border: 'none' }}>
                        <TableRow sx={{'& th':{color: '#8891ac' }}}>
                            <TableCell className="table-cell-limit" sx={{
                                '&::after': {
                                    display: 'none',
                                },
                                border: 'none',
                            }}>
                            {t("S/N")}
                        </TableCell>
                        <TableCell className="table-cell-limit" sx={{
                            '&::after': {
                                display: 'none',
                            },
                            border: 'none',
                        }}>
                            {t("Date")}
                        </TableCell>
                        <TableCell className="table-cell-limit" sx={{
                            '&::after': {
                                display: 'none',
                            },
                            border: 'none',
                        }}>
                            {t("Amount")}
                        </TableCell>
                        <TableCell className="table-cell-limit" sx={{
                            '&::after': {
                                display: 'none',
                            },
                            border: 'none',
                        }}></TableCell>
                        <TableCell className="table-cell-limit" sx={{
                            '&::after': {
                                display: 'none',
                            },
                            border: 'none',
                        }}></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>{rows}</TableBody>
            </Table>
        </TableContainer>
            {
        openPopup && (
            <RepaymentsForm
                RepaymentId={selectedItemId}
                open={openPopup}
                onClose={onCloseRepayments}
                handleResults={handleResults}
                calculationData={calculationData}
                contributions={contributions}
                repayments={repayments}
                updateRepayments={updateRepayments}
                handleUpdateRepayments={handleUpdateRepayments}
                repaymentsEdit={repaymentsEdit}
            />
        )
    }
        </Box >
    );
};

export default RepaymentsList;

